import { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAppSelector } from '@/store/hooks';
import { useI18nText } from '@/lib/utils/i18nText';
import { usePreviewMode } from '@/contexts/PreviewModeContext';
import { getCampaigns, getCampaignById, CAMPAIGN_CACHE_KEY_PREFIX } from '@/lib/api/campaign.api';
import { useCacheUpdated } from '@/lib/hooks/useCacheUpdated';
import {
  getInfluencerStatuses,
  getInfluencerCandidateGroups,
} from '@/lib/api/dashboard.api';
import { useCampaignInfluencerStatusRefreshSignal } from '@/lib/utils/campaignInfluencerStatusEvents';
import {
  createCandidateGroup,
  addToCandidateGroup,
  removeFromCandidateGroup,
  removeCandidateGroup,
} from '@/lib/api/communications.api';
import CampaignSelectDashboard from '@/components/CampaignSelectDashboard';
import { CampaignStatusFilter } from '@/pages/influencers/components/filters/CampaignStatusFilter';
import { CandidateGroupsFilter } from '@/pages/influencers/components/filters/CandidateGroupsFilter';
import { CampaignNavBox } from '@/components/CampaignNavBox';
import { CollapsedSelectSlot } from '@/components/CollapsedCampaignSidebar';
import ResizableSidebar, { type ResizableSidebarHandle } from '@/components/ResizableSidebar';
import { getAllowedStatuses } from '@/pages/influencers/utils';
import type { Campaign } from '@/pages/influencers/types';
import type { CampaignItem } from '@/components/CampaignSelect';

const MONGO_ID_RE = /\/([a-f0-9]{24})(?:\/|$)/;

type InfluencerStatuses = {
  byIdTotal?: Record<string, number>;
  byIdUnread?: Record<string, number>;
};

/** 이 경로에서 캠페인 컨텍스트 사이드바를 표시할지 판단 */
export function useShouldShowCampaignSidebar(): boolean {
  const location = useLocation();
  const { pathname } = location;
  if (!MONGO_ID_RE.test(pathname)) return false;
  // 대시보드는 자체 필터 사이드바 사용
  if (pathname.startsWith('/dashboard')) return false;
  // 패널 페이지는 캠페인 컨텍스트와 무관
  if (pathname.startsWith('/panel')) return false;
  // 인플루언서 프로필 페이지는 자체 레이아웃 사용
  if (/^\/influencer\/[a-f0-9]{24}(\/|$)/.test(pathname)) return false;
  // finances 페이지는 자체 캠페인 셀렉터 사이드바 사용
  if (pathname.startsWith('/finances')) return false;
  // admin edit page is not campaign-scoped
  if (pathname.startsWith('/admins/')) return false;
  // roster detail page has its own layout
  if (pathname.startsWith('/rosters/')) return false;
  // mailings detail uses an invitation ID in the path (not a campaign ID) and
  // opens its own drawer — the campaign context sidebar is irrelevant here
  if (pathname.startsWith('/mailings')) return false;
  return true;
}

export function CampaignContextSidebar() {
  const currentUser = useAppSelector((s) => s.envs.currentUser);
  const isPreviewMode = usePreviewMode();
  const isAdmin = currentUser?.role === 'admin' && !isPreviewMode;
  const { t } = useI18nText();
  const location = useLocation();
  const navigate = useNavigate();

  const campaignId = location.pathname.match(MONGO_ID_RE)?.[1] ?? null;
  // Reflect the active `?status=` filter (set by status deep-links / clicks) so the
  // sidebar highlights + checks the selected status instead of always looking unselected.
  const selectedStatuses = new URLSearchParams(location.search).getAll('status');
  const shouldShow = useShouldShowCampaignSidebar();
  const statusRefreshSignal = useCampaignInfluencerStatusRefreshSignal();

  const [campaigns, setCampaigns] = useState<CampaignItem[]>([]);
  const [campaignsLoading, setCampaignsLoading] = useState(true);
  const [campaign, setCampaign] = useState<Campaign | null>(null);
  const [influencerStatuses, setInfluencerStatuses] = useState<InfluencerStatuses | null>(null);
  const [candidateGroups, setCandidateGroups] = useState<Record<string, number>>({});
  const [candidateGroupsLoading, setCandidateGroupsLoading] = useState(false);
  // Collapsed sidebar → clicking a rail icon floats the real sidebar content
  // (CampaignSelectDashboard / CampaignNavBox / CampaignStatusFilter / CandidateGroupsFilter,
  // reused as-is) as an overlay instead of duplicating each section's form in a popover.
  const sidebarRef = useRef<ResizableSidebarHandle>(null);

  // 캠페인 목록 로드 — stale-first: an expired list paints immediately, and the refreshed
  // list is re-read (a cache hit) via useCacheUpdated once the background revalidation lands.
  const loadCampaignList = useCallback(() => {
    getCampaigns({}, { staleWhileRevalidate: true })
      .then((data) => setCampaigns(data.map((c) => ({ ...c, id: c.id ?? c._id }))))
      .catch(() => setCampaigns([]))
      .finally(() => setCampaignsLoading(false));
  }, []);
  useEffect(() => {
    setCampaignsLoading(true);
    loadCampaignList();
  }, [loadCampaignList]);

  // Once a stale-first read's background refresh has been written, pick up the fresh
  // campaign list / campaign (both re-reads are cache hits, and stale-first themselves so
  // they never block on the network).
  useCacheUpdated(CAMPAIGN_CACHE_KEY_PREFIX, () => {
    loadCampaignList();
    if (campaignId) {
      void getCampaignById(campaignId, { staleWhileRevalidate: true }).then((c) => {
        setCampaign((c as unknown as Campaign) ?? null);
      });
    }
  });

  // 현재 캠페인 데이터 + 인플루언서 상태 로드
  useEffect(() => {
    if (!campaignId) return;
    let cancelled = false;
    getCampaignById(campaignId, { staleWhileRevalidate: true }).then((c) => {
      if (!cancelled) setCampaign((c as unknown as Campaign) ?? null);
    });
    if (isAdmin) {
      getInfluencerStatuses({ campaignId }).then((resp) => {
        if (!cancelled) {
          const raw = resp as { total?: Record<string, number>; unread?: Record<string, number> } | null;
          setInfluencerStatuses(raw ? { byIdTotal: raw.total, byIdUnread: raw.unread } : null);
        }
      });
    }
    return () => { cancelled = true; };
  }, [campaignId, isAdmin, statusRefreshSignal]);

  // 후보자 그룹 로드
  const loadCandidateGroups = useCallback(async (cid: string) => {
    if (!isAdmin) return;
    setCandidateGroupsLoading(true);
    try {
      const resp = await getInfluencerCandidateGroups({ campaignId: cid });
      setCandidateGroups((resp as Record<string, number>) ?? {});
    } catch {
      setCandidateGroups({});
    } finally {
      setCandidateGroupsLoading(false);
    }
  }, [isAdmin]);

  useEffect(() => {
    if (campaignId) void loadCandidateGroups(campaignId);
  }, [campaignId, loadCandidateGroups]);

  // 캠페인 전환: 현재 URL의 campaignId를 새 ID로 교체
  const handleSelectCampaign = useCallback((id: string) => {
    if (!id || !campaignId) return;
    navigate(location.pathname.replace(campaignId, id));
  }, [campaignId, location.pathname, navigate]);

  // 후보자 그룹 CRUD
  const handleCreateGroup = useCallback(async (userIds: string[], groupName: string) => {
    if (!campaignId) return;
    await createCandidateGroup({ campaignId, userIds: userIds as unknown as number[], groupName });
    void loadCandidateGroups(campaignId);
  }, [campaignId, loadCandidateGroups]);

  const handleAddToGroup = useCallback(async (userIds: string[], groupName: string) => {
    if (!campaignId) return;
    await addToCandidateGroup({ campaignId, userIds: userIds as unknown as number[], groupName });
    void loadCandidateGroups(campaignId);
  }, [campaignId, loadCandidateGroups]);

  const handleRemoveFromGroup = useCallback(async (userIds: string[]) => {
    if (!campaignId) return;
    await removeFromCandidateGroup({ campaignId, userIds: userIds as unknown as number[] });
    void loadCandidateGroups(campaignId);
  }, [campaignId, loadCandidateGroups]);

  const handleRemoveGroup = useCallback(async (groupName: string) => {
    if (!campaignId) return;
    await removeCandidateGroup({ campaignId, groupName });
    void loadCandidateGroups(campaignId);
  }, [campaignId, loadCandidateGroups]);

  // Renames/reorders are drafted locally in CandidateGroupsFilter and committed as a batch —
  // this refreshes once after the whole batch instead of once per change (`currentGroupName` is
  // always "" here, since clicking a group navigates away rather than filtering in place, so
  // there's no active group filter to redirect on rename).
  const handleGroupsCommitted = useCallback(async () => {
    if (!campaignId) return;
    const updated = await getCampaignById(campaignId);
    setCampaign((updated as unknown as Campaign) ?? null);
    void loadCandidateGroups(campaignId);
  }, [campaignId, loadCandidateGroups]);

  if (!isAdmin || !shouldShow || !campaignId) return null;

  const campaignLabel = campaigns.find((c) => c._id === campaignId || c.id === campaignId)?.name ?? '…';
  const statuses = getAllowedStatuses(isAdmin);
  void statuses;
  const totalGroups = Object.keys(candidateGroups).length;

  return (
    <ResizableSidebar
      ref={sidebarRef}
      collapsedSlot={
        <CollapsedSelectSlot
          sections={[
            {
              key: 'campaign',
              icon: 'fa-gift',
              tooltip: t('Select Campaign', '캠페인 선택하기'),
              onIconClick: () => sidebarRef.current?.showOverlay(),
            },
            {
              key: 'nav',
              icon: 'fa-sitemap',
              tooltip: t('Navigation', '네비게이션'),
              onIconClick: () => sidebarRef.current?.showOverlay(),
            },
            ...(influencerStatuses ? [{
              key: 'status',
              icon: 'fa-users',
              tooltip: t('Influencer status', '인플루언서 상태'),
              onIconClick: () => sidebarRef.current?.showOverlay(),
            }] : []),
            ...((campaign || totalGroups > 0) ? [{
              key: 'groups',
              icon: 'fa-object-group',
              tooltip: t('Candidate groups', '후보 그룹'),
              onIconClick: () => sidebarRef.current?.showOverlay(),
            }] : []),
          ]}
        />
      }
    >
      <aside className="campaign-ctx-sidebar">
        <div className="dashboard-sidebar">
          <CampaignSelectDashboard
            campaigns={campaigns}
            campaignsLoading={campaignsLoading}
            selectedCampaignId={campaignId}
            selectedCampaignLabel={campaignLabel}
            onSelectCampaign={handleSelectCampaign}
          />

          {/* 페이지 네비게이션 */}
          <CampaignNavBox campaignId={campaignId} campaignStatus={campaign?.status} />

          {/* 인플루언서 상태 */}
          {influencerStatuses && (
            <CampaignStatusFilter
              statuses={statuses}
              influencerStatuses={influencerStatuses}
              selectedStatuses={selectedStatuses}
              onStatusClick={(status) => {
                if (campaignId) navigate(`/dashboard/campaign/${campaignId}?status=${status}`);
              }}
              t={t}
            />
          )}

          {/* 후보자 그룹 */}
          <CandidateGroupsFilter
            campaign={campaign}
            candidateGroups={candidateGroups}
            candidateGroupsLoading={candidateGroupsLoading}
            currentGroupName=""
            onGroupClick={(groupName) => {
              if (campaignId) navigate(`/dashboard/campaign/${campaignId}?candidateGroupName=${encodeURIComponent(groupName)}`);
            }}
            onCreateGroup={handleCreateGroup}
            onAddToGroup={handleAddToGroup}
            onRemoveFromGroup={handleRemoveFromGroup}
            onRemoveGroup={handleRemoveGroup}
            onGroupsCommitted={handleGroupsCommitted}
            selectedUserIds={new Set<string>()}
            influencers={[]}
            selectedCampaignId={campaignId}
            t={t}
          />
        </div>
      </aside>
    </ResizableSidebar>
  );
}
